import { CheckIcon } from '@heroicons/react/24/solid'
import { useTranslation } from 'react-i18next'

import { getPasswordPolicyRequirements, isPasswordRequirementMet } from '@authy/lib-common/utils/password'
import type { PasswordStrength } from '@authy/lib-common/entities'

type PasswordRequirementsProps = {
    password: string
    strength: PasswordStrength
    minLength: number
}

export const PasswordRequirements = ({ password, strength, minLength }: PasswordRequirementsProps) => {
    const { t } = useTranslation()

    const requirements = getPasswordPolicyRequirements(strength, minLength)

    if (!password.length || requirements.length === 0) {
        return null
    }

    return (
        <div className="space-y-2">
            <p className="text-account-secondary text-xs font-semibold uppercase tracking-[0.12em]">
                {t('password.requirementsTitle')}
            </p>
            <ul className="space-y-2">
                {requirements.map((requirement) => {
                    const met = isPasswordRequirementMet(requirement.id, password, minLength)

                    return (
                        <li key={requirement.id} className="flex items-start gap-3 text-sm">
                            <span
                                className={[
                                    'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                                    met
                                        ? 'border-account-success-border bg-account-success-soft text-account-success'
                                        : 'border-account-border bg-account-surface text-transparent',
                                ].join(' ')}
                                aria-hidden="true"
                            >
                                <CheckIcon className="h-3 w-3" />
                            </span>
                            <span className={met ? 'text-account-faint line-through' : 'text-account-secondary'}>
                                {t(`password.requirements.${requirement.id}`, {
                                    minLength,
                                })}
                            </span>
                        </li>
                    )
                })}
            </ul>
        </div>
    )
}
